import {Injectable}                                               from '@angular/core';
import {filter, map}                                              from 'rxjs/operators';
import {Observable}                                               from 'rxjs';
import {ApTimeSpanUtil}                                           from '../../../ap-utils';
import {
  ApDynformsConfigFieldset
}                                                                 from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigDatepicker
}                                                                 from '../../../ap-dynforms/config/ap-dynforms-config-datepicker';
import {nameOf}                                                   from '../../../ap-core/utils/ap-name-of';
import {
  ApDynformsConfigComboBox
}                                                                 from '../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {
  ApDynformsConfigPlaceholder
}                                                                 from '../../../ap-dynforms/config/ap-dynforms-config-placeholder';
import {CampaignYearStore}                                        from '../../../stores/login/campaignyear.store';
import {DriverStore}                                              from '../../../stores/docu/driver.store';
import {MachineStore}                                             from '../../../stores/docu/machine.store';
import {InstrumentStore}                                          from '../../../stores/docu/instrument.store';
import {ModalDialogStore}                                         from '../../../stores/dialog/modal.dialog.store';
import {
  ApDynformsConfigNumerictextbox
}                                                                 from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {
  ApTranslationService
}                                                                 from '../../../ap-utils/service/ap-translation.service';
import {IPpEntryPlanBookingEditData, IPpEntryPlanBookingGridItem} from './pp-plan-booking.types';
import IDriver = Data.DocuContext.Driver.IDriver;
import IMachine = Data.DocuContext.Machine.IMachine;
import IInstrument = Data.DocuContext.Instrument.IInstrument;

@Injectable()
export class PpPlanBookingFieldsConfig {
  constructor(private driverStore: DriverStore,
              private machineStore: MachineStore,
              private instrumentStore: InstrumentStore,
              private modalDialogStore: ModalDialogStore,
              private campaignYearStore: CampaignYearStore,
              private translationService: ApTranslationService) {
  }

  /**
   * Transforms a grid item object into an editable format for plan booking purposes.
   * @param gridItem - The grid item containing information about the plan booking entry.
   * @return An editable object representing the plan booking entry data.
   * @public
   */
  public getEditObject(gridItem: IPpEntryPlanBookingGridItem): IPpEntryPlanBookingEditData {
    const durationInTimeSpan = ApTimeSpanUtil.getTimeSpanFromMilliseconds(gridItem.DurationInMilliseconds, true);
    return {
      ApplicationDate: gridItem.ApplicationDate,
      MachineId: gridItem.MachineId,
      DriverId: gridItem.DriverId,
      InstrumentId: gridItem.InstrumentId,
      DurationInHours: durationInTimeSpan.TotalHours,
      HaPerHour: gridItem.HaPerHour,
    };
  }

  /**
   * Generates and returns an array of modal fieldsets to configure an entry plan booking.
   * @param gridItem - Details of the grid item such as area and related information.
   * @param editData - Current edit data for the booking, containing pre-filled or default values.
   * @param modalDialogChanges$ - Observable providing updates to booking edit data during modal interaction.
   * @return Array of fieldsets used to configure the entry plan booking in the modal dialog.
   * @public
   */
  public getModalFieldSets(gridItem: IPpEntryPlanBookingGridItem, editData: IPpEntryPlanBookingEditData,
                           modalDialogChanges$: Observable<IPpEntryPlanBookingEditData>): ApDynformsConfigFieldset[] {
    const campaignYearStart = new Date(this.campaignYearStore.getSelectedCampaignYear().DefaultStart);
    const campaignYearEnd = new Date(this.campaignYearStore.getSelectedCampaignYear().DefaultEnd);
    return [
      new ApDynformsConfigFieldset({
        legend: 'Docu_Ini__Time',
        key: 'EditTime',
        useMaxWidth: true,
        config: [
          new ApDynformsConfigDatepicker({
            key: nameOf<IPpEntryPlanBookingEditData>('ApplicationDate'),
            label: 'Global_Application_Date',
            value: editData.ApplicationDate,
            minDate: campaignYearStart,
            maxDate: campaignYearEnd
          }),
          new ApDynformsConfigPlaceholder()
        ]
      }),
      new ApDynformsConfigFieldset({
        legend: 'Settings__Lbl_Menu_Resources',
        key: 'EditResources',
        useMaxWidth: true,
        config: [
          new ApDynformsConfigComboBox({
            key: nameOf<IPpEntryPlanBookingEditData>('MachineId'),
            label: 'Base__Engine',
            valueField: nameOf<IMachine>('Id'),
            textField: nameOf<IMachine>('NameWithSign'),
            value: editData.MachineId,
            options: this.machineStore.Machines$,
            valuePrimitive: true,
            sort: 1
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IPpEntryPlanBookingEditData>('InstrumentId'),
            label: 'Docu_Ini__Tool',
            valueField: nameOf<IInstrument>('Id'),
            textField: nameOf<IInstrument>('Name'),
            value: editData.InstrumentId,
            value$: this.modalDialogStore.listenChanges().pipe(
              filter((values: IPpEntryPlanBookingEditData) => !!values.InstrumentId || !!values.MachineId),
              map((values: IPpEntryPlanBookingEditData) => {
                if (values.InstrumentId) {
                  return values.InstrumentId;
                }
                const formValues = this.modalDialogStore.getFormValues() as IPpEntryPlanBookingEditData;
                if (formValues.InstrumentId) {
                  return formValues.InstrumentId;
                }
                if (values.MachineId) {
                  const machine = this.machineStore.getMachineById(values.MachineId);
                  return machine?.DefaultInstrument?.Id;
                }
                return undefined;
              })
            ),
            options: this.instrumentStore.Instruments$,
            valuePrimitive: true,
            sort: 1
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IPpEntryPlanBookingEditData>('DriverId'),
            label: 'Docu_Ini__Motorist',
            valueField: nameOf<IDriver>('Id'),
            textField: nameOf<IDriver>('FirstNameFullName'),
            value: editData.DriverId,
            value$: this.modalDialogStore.listenChanges().pipe(
              filter((values: IPpEntryPlanBookingEditData) => !!values.DriverId || !!values.MachineId),
              map((values: IPpEntryPlanBookingEditData) => {
                if (!!values.DriverId) {
                  return values.DriverId;
                }
                const formValues = this.modalDialogStore.getFormValues() as IPpEntryPlanBookingEditData;
                if (!!formValues.DriverId) {
                  return formValues.DriverId;
                }
                if (!!values.MachineId) {
                  const machine = this.machineStore.getMachineById(values.MachineId);
                  return machine?.DefaultDriver?.Id;
                }
                return undefined;
              })
            ),
            options: this.driverStore.Drivers$,
            valuePrimitive: true,
            sort: 1
          }),
          new ApDynformsConfigPlaceholder(),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<IPpEntryPlanBookingEditData>('DurationInHours'),
            label: `${this.translationService.translate('Docu_Ini__Duration')} [${this.translationService.translate('Base__UnitH')}]`,
            value: editData.DurationInHours,
            decimals: 1,
            min: 0,
            step: 0.1
          }),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<IPpEntryPlanBookingEditData>('HaPerHour'),
            label: `${this.translationService.translate('FieldAreaPerformance')} [${this.translationService.translate('Unit_ha_per_h')}]`,
            value: editData.HaPerHour,
            value$: modalDialogChanges$.pipe(
              map(c => {
                if (!Number.isFinite(gridItem.AreaHa) || !Number.isFinite(c.DurationInHours)) {
                  return 0;
                }
                const result = gridItem.AreaHa / c.DurationInHours;
                return Number.isFinite(result) ? result : 0;
              })
            ),
            decimals: 1,
            min: 0,
            step: 0.1,
            disabled: true
          })
        ]
      })
    ];
  }

  /**
   * Generates and returns an array of configuration fieldsets for a pager form.
   * Each fieldset contains specific settings and controls to configure dynamic form input parameters.
   * The method uses predefined data sources, translations, and configurations to build the fieldsets.
   * @return An array of dynamic form fieldsets configured with specific settings and controls.
   * @public
   */
  public getPagerFieldSets(): ApDynformsConfigFieldset[] {
    const campaignYearStart = new Date(this.campaignYearStore.getSelectedCampaignYear().DefaultStart);
    const campaignYearEnd = new Date(this.campaignYearStore.getSelectedCampaignYear().DefaultEnd);
    return [
      new ApDynformsConfigFieldset({
        legend: 'Docu_Ini__Time',
        key: 'Time',
        useMaxWidth: true,
        config: [
          new ApDynformsConfigDatepicker({
            key: nameOf<IPpEntryPlanBookingEditData>('ApplicationDate'),
            label: 'Global_Application_Date',
            minDate: campaignYearStart,
            maxDate: campaignYearEnd
          }),
          new ApDynformsConfigPlaceholder()
        ]
      }),
      new ApDynformsConfigFieldset({
        legend: 'Settings__Lbl_Menu_Resources',
        key: 'Resources',
        useMaxWidth: true,
        config: [
          new ApDynformsConfigComboBox({
            key: nameOf<IPpEntryPlanBookingEditData>('MachineId'),
            label: 'Base__Engine',
            valueField: nameOf<IMachine>('Id'),
            textField: nameOf<IMachine>('NameWithSign'),
            options: this.machineStore.Machines$,
            valuePrimitive: true,
            sort: 1
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IPpEntryPlanBookingEditData>('InstrumentId'),
            label: 'Docu_Ini__Tool',
            valueField: nameOf<IInstrument>('Id'),
            textField: nameOf<IInstrument>('Name'),
            options: this.instrumentStore.Instruments$,
            valuePrimitive: true,
            sort: 1
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IPpEntryPlanBookingEditData>('DriverId'),
            label: 'Docu_Ini__Motorist',
            valueField: nameOf<IDriver>('Id'),
            textField: nameOf<IDriver>('FirstNameFullName'),
            options: this.driverStore.Drivers$,
            valuePrimitive: true,
            sort: 1
          }),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<IPpEntryPlanBookingEditData>('HaPerHour'),
            label: `${this.translationService.translate('FieldAreaPerformance')} [${this.translationService.translate('Unit_ha_per_h')}]`,
            decimals: 1,
            min: 0,
            step: 0.1
          })
        ]
      })
    ];
  }
}
